/* ═══════════════════════════════════════════════════════════════════
   Greenway Biotech — Product & Blog Page Scripts (companion to dispatcher)
   ----------------------------------------------------------------
   Filename:    gwb-product-scripts.js
   Version:     1.2.1
   Loaded by:   theme.liquid, AFTER gwb-calculators.js
   Specs:       Fertilizer Product Page Specification v3.x
                Personal Care & Wellness Specification v2.0.3
                Blog Article Specification v1.0
   Purpose:     Page behavior for v3 / v2.0.3 product pages AND v3 blog
                articles (custom.long_description / blog body HTML contains
                NO inline scripts — this file owns all page behavior).

   What this script does:
   1. Scroll-spy: watches the v3 sub-nav sections and toggles the .active
      class on the matching .v3-subnav-links anchor as the user scrolls.
      Section IDs are now DISCOVERED from the sub-nav's own anchor hrefs
      (#id), so this works for BOTH product pages (v3-why, v3-coverage,
      v3-rates, v3-calc, v3-compare, v3-faq) and blog articles (v3-intro,
      v3-how, v3-shop, v3-faq, v3-related, etc.) with no per-page config.
   2. Smooth-scroll: intercepts clicks on sub-nav links and scrolls with an
      offset for the Shopify theme header (--gwb-header-offset) + the sticky
      sub-nav height (56px).
   3. Band 7 Application-Rates tabs: wires
      .gwb-tabs[data-tabs="application-rates"] tab switching for v2.0.3
      personal-care / wellness pages with no companion calculator JS file.
      A per-wrapper guard (data-tabs-wired="1") prevents double-binding.

   Multi-page safety:
   - Scroll-spy / smooth-scroll: run only if a .v3-subnav element exists.
     Silently no-op on non-v3 pages.
   - Rate tabs: run only if at least one .gwb-tabs[data-tabs=
     "application-rates"] wrapper exists, and skip any wrapper already
     tagged data-tabs-wired="1".
   - Idempotent: safe to load twice (boot guard at top).

   Version history:
   - 1.2.1 (Aug 2026): HEADER-OFFSET AUTO-MEASURE. Fixes the v3.0.1 CSS
     regression where the sticky .v3-subnav pinned at top:0 UNDERNEATH the
     theme's sticky header and appeared not to stick at all. Root cause:
     .v3-subnav sticks at top: var(--gwb-header-offset), the stylesheet
     pins that variable to 0px on .gwb-style, and the documented per-theme
     override was never wired; the old z-index of 90 masked this by
     painting the bar OVER the header (the dropdown bug 3.0.1 fixed).
     This script now measures the theme elements actually pinned to the
     top of the viewport (fixed/sticky, occluding the top strip) on boot,
     scroll, and resize, and writes the result as an INLINE custom
     property on every .gwb-style element. Inline custom properties beat
     the stylesheet's `.gwb-style { --gwb-header-offset: 0px }` rule, so
     NO CSS edit is required (gwb-product-styles stays at v3.0.1).
     Heals all four consumers of the variable at once: .v3-subnav sticky
     top, .v3-rates-tabs sticky top, smooth-scroll target offset, and the
     scroll-spy baseline (spy geometry now reads the offset live; the
     IntersectionObserver rootMargin keeps its boot-time value, which is
     a coarse pre-filter only). Boot order runs the measure first.
   - 1.2.0 (May 2026): Scroll-spy section IDs are now DISCOVERED from the
     sub-nav anchors instead of a hardcoded product-only list. This makes
     active-link highlighting work on v3 blog articles (v3-intro, v3-shop,
     v3-related, etc.) as well as product pages. FALLBACK_SECTION_IDS kept
     for legacy markup that lacks anchor hrefs. No HTML changes required.
   - 1.1.0 (May 2026): Added Band 7 Application-Rates tab switcher.
   - 1.0.0: Initial scroll-spy + smooth-scroll release.

   Browser support:
   - Uses IntersectionObserver (Chrome 51+, Firefox 55+, Safari 12.1+,
     Edge 15+). Graceful no-op if unavailable — clicks still work via
     native anchor behavior; only the active-highlight is lost.
   ═══════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  // ─── Configuration ─────────────────────────────────────────────────
  // Fallback only — used if the sub-nav has no resolvable anchor hrefs.
  // Normal operation DISCOVERS IDs from the sub-nav links (see discover()).
  var FALLBACK_SECTION_IDS = ['v3-why', 'v3-coverage', 'v3-rates', 'v3-calc', 'v3-compare', 'v3-faq'];
  var SUBNAV_HEIGHT = 56;  // matches .v3-subnav-inner height in gwb-style.css

  function getHeaderOffset() {
    // Read --gwb-header-offset from the .gwb-style element (theme-level override)
    var root = document.querySelector('.gwb-style');
    if (!root) return 0;
    var v = getComputedStyle(root).getPropertyValue('--gwb-header-offset').trim();
    if (!v) return 0;
    var n = parseFloat(v);
    return isFinite(n) ? n : 0;
  }

  // ─── Section + link discovery ──────────────────────────────────────
  // Derive the ordered list of section IDs from the sub-nav's own anchors.
  // This is what makes the script work for both product pages and blog
  // articles without a hardcoded per-page list. We only keep an entry when
  // BOTH the anchor and a matching element with that id actually exist, so
  // a stray link to a missing section is ignored rather than breaking spy.
  function discover() {
    var subnav = document.querySelector('.v3-subnav');
    if (!subnav) return null;

    var anchors = subnav.querySelectorAll('.v3-subnav-links a[href^="#"]');

    // Build the candidate ID order from the anchors; fall back to the
    // legacy product-page list if the sub-nav exposes no usable hrefs.
    var orderedIds = [];
    anchors.forEach(function (a) {
      var href = a.getAttribute('href') || '';
      var id = href.charAt(0) === '#' ? href.slice(1) : '';
      if (id && orderedIds.indexOf(id) === -1) {
        orderedIds.push(id);
      }
    });
    if (orderedIds.length === 0) {
      orderedIds = FALLBACK_SECTION_IDS.slice();
    }

    var sections = [];
    var links = {};
    var ids = [];
    var foundCount = 0;

    orderedIds.forEach(function (id) {
      var section = document.getElementById(id);
      var link = subnav.querySelector('.v3-subnav-links a[href="#' + id + '"]');
      if (section && link) {
        sections.push(section);
        links[id] = link;
        ids.push(id);          // preserves sub-nav order for checkTop()
        foundCount++;
      }
    });

    if (foundCount === 0) return null;
    return { subnav: subnav, sections: sections, links: links, ids: ids };
  }

  // ─── Active-link toggling ──────────────────────────────────────────
  function setActive(links, activeId) {
    Object.keys(links).forEach(function (id) {
      links[id].classList.toggle('active', id === activeId);
    });
  }

  // ─── Scroll-spy via IntersectionObserver ───────────────────────────
  function initScrollSpy(ctx) {
    if (typeof IntersectionObserver === 'undefined') {
      console.warn('GWB: IntersectionObserver unavailable — scroll-spy disabled');
      return;
    }

    var headerOffset = getHeaderOffset();
    var topMargin = -(headerOffset + SUBNAV_HEIGHT + 8);  // small visual buffer
    var bottomMargin = -(Math.floor(window.innerHeight * 0.55));  // 55% from bottom

    var observer = new IntersectionObserver(function (entries) {
      var visibleEntries = entries.filter(function (e) { return e.isIntersecting; });
      if (visibleEntries.length === 0) return;

      // Among all sections currently intersecting, pick the topmost section
      // that's past the sub-nav baseline.
      var liveOffset = getHeaderOffset();  // v1.2.1: track the measured value
      var allIntersecting = ctx.sections.filter(function (sec) {
        var rect = sec.getBoundingClientRect();
        return rect.top <= (liveOffset + SUBNAV_HEIGHT + 50) &&
               rect.bottom >= (liveOffset + SUBNAV_HEIGHT);
      });

      var current = allIntersecting.length > 0
        ? allIntersecting[allIntersecting.length - 1]  // topmost-past-the-nav
        : visibleEntries[0].target;

      setActive(ctx.links, current.id);
    }, {
      root: null,  // viewport
      rootMargin: topMargin + 'px 0px ' + bottomMargin + 'px 0px',
      threshold: [0, 0.01, 0.5, 1]
    });

    ctx.sections.forEach(function (sec) { observer.observe(sec); });

    // Edge case: when scrolled to the very top, highlight the first
    // discovered section (sub-nav order), not a hardcoded ID.
    var firstId = ctx.ids[0];
    function checkTop() {
      if (window.scrollY < 50) {
        setActive(ctx.links, firstId);
      }
    }
    window.addEventListener('scroll', checkTop, { passive: true });
    checkTop();
  }

  // ─── Smooth-scroll on sub-nav click with offset ─────────────────────
  function initSmoothScroll(ctx) {
    Object.keys(ctx.links).forEach(function (id) {
      var link = ctx.links[id];
      link.addEventListener('click', function (e) {
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();

        var headerOffset = getHeaderOffset();
        var offset = headerOffset + SUBNAV_HEIGHT;
        var targetTop = target.getBoundingClientRect().top + window.scrollY - offset;

        window.scrollTo({
          top: targetTop,
          behavior: 'smooth'
        });

        setActive(ctx.links, id);

        if (history.replaceState) {
          history.replaceState(null, '', '#' + id);
        }
      });
    });
  }

  // ─── Band 7: Application-Rates tab switcher ────────────────────────
  function applyRatesTabState(root, targetContent) {
    root.querySelectorAll('.calc-tab').forEach(function (t) {
      var isTarget = t.dataset.content === targetContent;
      t.classList.toggle('active', isTarget);
      t.setAttribute('aria-selected', String(isTarget));
    });
    root.querySelectorAll('.calc-section').forEach(function (section) {
      var isTarget = section.dataset.content === targetContent;
      section.classList.toggle('active', isTarget);
      section.style.display = isTarget ? 'block' : 'none';
      if (isTarget) {
        section.removeAttribute('hidden');
      } else {
        section.setAttribute('hidden', '');
      }
    });
  }

  function initApplicationRateTabs() {
    document.querySelectorAll('.gwb-tabs[data-tabs="application-rates"]').forEach(function (root) {
      if (root.dataset.tabsWired === '1') return;
      root.dataset.tabsWired = '1';

      var initialTarget = null;
      var activeTab = root.querySelector('.calc-tab.active');
      if (activeTab) initialTarget = activeTab.dataset.content;
      if (!initialTarget) {
        var firstTab = root.querySelector('.calc-tab');
        if (firstTab) initialTarget = firstTab.dataset.content;
      }
      if (initialTarget) applyRatesTabState(root, initialTarget);

      root.querySelectorAll('.calc-tab').forEach(function (tab) {
        tab.addEventListener('click', function () {
          applyRatesTabState(root, tab.dataset.content);
        });
        tab.addEventListener('keypress', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            applyRatesTabState(root, tab.dataset.content);
          }
        });
      });
    });
  }

  window.gwbInitApplicationRateTabs = initApplicationRateTabs;

  // ─── Header-offset auto-measure (v1.2.1) ──────────────────────────
  // Measures how much of the top of the viewport the theme's pinned
  // header actually occludes, and writes it to --gwb-header-offset as an
  // inline custom property on every .gwb-style element. Re-measures on
  // scroll (rAF-throttled) and resize, so scroll-away announcement bars
  // and shrink-on-scroll headers self-correct the moment they change —
  // exactly when the sticky sub-nav needs the value. If the theme header
  // is NOT sticky, nothing is pinned while scrolled and the offset
  // settles at 0, which is correct.
  function initHeaderOffset() {
    var roots = document.querySelectorAll('.gwb-style');
    if (!roots.length) return;

    var pending = false;

    function measure() {
      pending = false;
      var bottom = 0;
      var els = document.querySelectorAll(
        'header, [class*="header"], [id*="header"], ' +
        '[class*="announcement"], [class*="Announcement"], ' +
        '[id*="shopify-section-header"]'
      );
      for (var i = 0; i < els.length; i++) {
        var el = els[i];
        // Never measure our own bars or anything inside the article.
        if (el.closest && el.closest('.gwb-style')) continue;
        var cs = getComputedStyle(el);
        if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;
        if (cs.display === 'none' || cs.visibility === 'hidden') continue;
        var r = el.getBoundingClientRect();
        if (r.height <= 0 || r.height > 320) continue;  // sanity: not a page wrapper
        if (r.top <= 1 && r.bottom > bottom) bottom = r.bottom;  // pinned at the top NOW
      }
      bottom = Math.max(0, Math.round(bottom));
      for (var j = 0; j < roots.length; j++) {
        roots[j].style.setProperty('--gwb-header-offset', bottom + 'px');
      }
    }

    function schedule() {
      if (pending) return;
      pending = true;
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(measure);
      } else {
        setTimeout(measure, 16);
      }
    }

    measure();  // initial value before spy/scroll wiring reads it
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    window.addEventListener('load', schedule);
  }

  // ─── Boot ──────────────────────────────────────────────────────────
  function boot() {
    if (window.__gwbProductScriptsBooted) return;
    window.__gwbProductScriptsBooted = true;

    initHeaderOffset();

    var ctx = discover();
    if (ctx) {
      initScrollSpy(ctx);
      initSmoothScroll(ctx);
      console.log('✅ GWB: Scroll-spy active on ' + ctx.sections.length +
                  ' sections [' + ctx.ids.join(', ') + ']');
    }

    if (document.querySelector('.gwb-tabs[data-tabs="application-rates"]')) {
      initApplicationRateTabs();
      console.log('✅ GWB: Application-Rates tabs initialized');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();